import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../../api';
import type { WordPressCheckpoint } from '../../../api';
import { useResource } from '../../../hooks';
import { ErrorNotice, LoadingRows } from '../../../components/ui';
import { IconButton, Section, Table } from '../../../components/primitives';
import { ProjectSection, QuietEmpty } from '../../../components/project/ui';
import { formatBytes, relativeTime } from '../../../components/project/projectHealth';
import { useProject } from '../ProjectContext';

/**
 * WordPressRecoverySection answers "can we recover?" for a WordPress shop
 * (UX-15): the verified database checkpoints Commerce Ops took before
 * updates and deployments, and how each is restored. Restoring is never
 * automatic — orders placed after a checkpoint would be lost — so the
 * restore is shown as the exact commands to run on the server.
 */
export function WordPressRecoverySection() {
  const project = useProject();
  const { environment } = project;
  const recovery = useResource(
    () => (environment ? api.getWordPressRecovery(environment.id) : Promise.resolve(null)),
    [environment?.id],
    environment?.id, { key: `env:${environment?.id}:wordpress-recovery` },
  );

  if (!environment) {
    return (
      <ProjectSection>
        <QuietEmpty testId="no-environment">
          No environments
          <Link className="button button--primary button--small quiet-empty__action" to={project.sectionPath('settings')}>
            Add an environment
          </Link>
        </QuietEmpty>
      </ProjectSection>
    );
  }

  const checkpoints = recovery.data?.checkpoints ?? [];
  return (
    <ProjectSection>
      <Section title={`Recovery for ${environment.name}`} testId="wordpress-recovery">
        <ErrorNotice error={recovery.error} />
        {recovery.loading && !recovery.data ? <LoadingRows label="Reading the checkpoints" rows={3} rowHeight="32px" /> : null}
        {recovery.data && checkpoints.length === 0 ? (
          <QuietEmpty testId="no-checkpoints">No checkpoints</QuietEmpty>
        ) : null}
        {checkpoints.length > 0 ? (
          <Table label="Database checkpoints">
            <thead>
              <tr>
                <th scope="col">Taken</th>
                <th scope="col">Why</th>
                <th scope="col">Size</th>
                <th scope="col">Verified</th>
                <th scope="col">
                  <span className="visually-hidden">Restore</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {checkpoints.map((checkpoint) => (
                <CheckpointRow key={`${checkpoint.execution_id}-${checkpoint.path}`} checkpoint={checkpoint} />
              ))}
            </tbody>
          </Table>
        ) : null}
      </Section>
    </ProjectSection>
  );
}

function CheckpointRow({ checkpoint }: { checkpoint: WordPressCheckpoint }) {
  const [open, setOpen] = useState(false);
  const detailsId = `restore-${checkpoint.execution_id}`;
  return (
    <>
      <tr data-testid="wordpress-checkpoint">
        <th scope="row" className="wp-recovery__taken">
          {new Date(checkpoint.taken_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
          <div className="table__sub">{relativeTime(checkpoint.taken_at)}</div>
        </th>
        <td>{checkpoint.reason}</td>
        <td>
          {formatBytes(checkpoint.bytes)}
          <div className="table__sub">{checkpoint.tables} tables</div>
        </td>
        <td>
          {checkpoint.complete ? 'Export complete' : 'Exported; completeness not confirmed'}
          <div className="table__sub font-mono" title={checkpoint.sha256}>
            sha256 {checkpoint.sha256.slice(0, 12)}…
          </div>
        </td>
        <td>
          <button type="button" className="button button--secondary button--small" aria-expanded={open} aria-controls={detailsId} onClick={() => setOpen((v) => !v)}>
            {open ? 'Hide the commands' : 'How to restore'}
          </button>
        </td>
      </tr>
      {open ? (
        <tr id={detailsId} className="wp-recovery__commands">
          <td colSpan={5}>
            <p className="type-description" data-testid="wordpress-recovery-manual">
              Manual restore, in order, on the server. The second command keeps the current database. Orders and changes
              since this checkpoint ({relativeTime(checkpoint.taken_at)}) are lost.
            </p>
            <ol className="interpretation__steps">
              {checkpoint.restore_commands.map((command) => (
                <li key={command}>
                  <div className="interpretation__command">
                    <code>{command}</code>
                    <IconButton icon="copy" label="Copy the command" onClick={() => void navigator.clipboard?.writeText(command)} />
                  </div>
                </li>
              ))}
            </ol>
          </td>
        </tr>
      ) : null}
    </>
  );
}
